/**
 * Safe date helpers. Every function accepts whatever the backend (or a form) hands over and never throws:
 * invalid input yields null from parseDate and the fallback string from the format functions.
 *
 * Formats the app receives:
 *  - "dd-MM-yyyy HH:mm:ss" (Jackson pattern on BaseEntity.created_on / updated_on, GMT+5:30)
 *  - "dd/MM/yyyy", "dd-MM-yyyy" (date only), ISO-8601 strings, "yyyy-MM-dd HH:mm:ss"
 *  - [year, month, day, hour, minute, second, nano] arrays (LocalDateTime / LocalDate)
 *  - Unix timestamps in seconds or milliseconds, Date objects, { $date } and { seconds } objects
 */

export const DATE_LOCALE = "en-IN";
export const DATE_FALLBACK = "—";

const IST_OFFSET_MINUTES = 330;
const MS_PER_MINUTE = 60 * 1000;
// Anything below this is treated as a timestamp in seconds (1e11 s is the year 5138, 1e11 ms is 1973)
const SECONDS_LIMIT = 1e11;

const isValidDateObject = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const fromTimestamp = (number) => {
  if (!Number.isFinite(number)) return null;
  const date = new Date(Math.abs(number) < SECONDS_LIMIT ? number * 1000 : number);
  return isValidDateObject(date) ? date : null;
};

// Builds a local date and rejects overflow such as 31-02-2026 (which JS would silently roll over)
const fromParts = (year, month, day, hour = 0, minute = 0, second = 0, asIst = false) => {
  const [y, mo, d, h, mi, s] = [year, month, day, hour, minute, second].map(Number);
  if ([y, mo, d, h, mi, s].some((n) => !Number.isFinite(n))) return null;
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) return null;

  const date = asIst
    ? new Date(Date.UTC(y, mo - 1, d, h, mi, s) - IST_OFFSET_MINUTES * MS_PER_MINUTE)
    : new Date(y, mo - 1, d, h, mi, s);

  if (!isValidDateObject(date)) return null;
  const check = asIst ? new Date(date.getTime() + IST_OFFSET_MINUTES * MS_PER_MINUTE) : date;
  const [cy, cmo, cd] = asIst
    ? [check.getUTCFullYear(), check.getUTCMonth() + 1, check.getUTCDate()]
    : [check.getFullYear(), check.getMonth() + 1, check.getDate()];
  return cy === y && cmo === mo && cd === d ? date : null;
};

const DAY_FIRST = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
const YEAR_FIRST = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?$/i;

const parseString = (input) => {
  const text = input.trim();
  if (!text || /^(null|undefined|nan|invalid date)$/i.test(text)) return null;

  if (/^-?\d+(\.\d+)?$/.test(text) && text.replace(/\D/g, "").length >= 9) {
    return fromTimestamp(Number(text));
  }

  const dayFirst = text.match(DAY_FIRST);
  if (dayFirst) {
    const [, d, mo, y, h, mi, s] = dayFirst;
    // A time part means the backend's GMT+5:30 timestamp; a bare date stays a local calendar date
    return fromParts(y, mo, d, h ?? 0, mi ?? 0, s ?? 0, h !== undefined);
  }

  const yearFirst = text.match(YEAR_FIRST);
  if (yearFirst) {
    const [, y, mo, d, h, mi, s, zone] = yearFirst;
    if (zone) {
      const date = new Date(
        `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}T${String(h ?? 0).padStart(2, "0")}:${mi ?? "00"}:${s ?? "00"}${
          zone.toUpperCase() === "Z" ? "Z" : zone.replace(/^([+-]\d{2})(\d{2})$/, "$1:$2")
        }`
      );
      return isValidDateObject(date) ? date : null;
    }
    return fromParts(y, mo, d, h ?? 0, mi ?? 0, s ?? 0);
  }

  const date = new Date(text);
  return isValidDateObject(date) ? date : null;
};

/** Returns a valid Date or null. Never throws. */
export const parseDate = (value) => {
  try {
    if (value === null || value === undefined || value === "") return null;
    if (value instanceof Date) return isValidDateObject(value) ? new Date(value.getTime()) : null;
    if (typeof value === "number") return fromTimestamp(value);
    if (typeof value === "string") return parseString(value);

    if (Array.isArray(value)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = value;
      return fromParts(year, month, day, hour, minute, second);
    }

    if (typeof value === "object") {
      if (value.$date !== undefined) return parseDate(value.$date);
      if (value.seconds !== undefined) return fromTimestamp(Number(value.seconds));
      if (value._seconds !== undefined) return fromTimestamp(Number(value._seconds));
      if (typeof value.toDate === "function") return parseDate(value.toDate());
    }
  } catch {
    /* fall through */
  }
  return null;
};

export const isValidDate = (value) => parseDate(value) !== null;

/** Milliseconds since epoch, or null when the value is not a valid date. */
export const toTimestamp = (value) => {
  const date = parseDate(value);
  return date ? date.getTime() : null;
};

const format = (value, options, fallback) => {
  const date = parseDate(value);
  if (!date) return fallback;
  try {
    return new Intl.DateTimeFormat(DATE_LOCALE, options).format(date);
  } catch {
    return fallback;
  }
};

const DEFAULT_DATE_OPTIONS = { day: "numeric", month: "short", year: "numeric" };

/** "31 Oct 2026". Pass `options` (Intl.DateTimeFormat) and/or `fallback` to customise. */
export const formatDate = (value, { fallback = DATE_FALLBACK, ...options } = {}) =>
  format(value, Object.keys(options).length ? options : DEFAULT_DATE_OPTIONS, fallback);

/** "31 Oct 2026, 10:30 AM" */
export const formatDateTime = (value, { fallback = DATE_FALLBACK } = {}) => {
  const text = format(
    value,
    { ...DEFAULT_DATE_OPTIONS, hour: "numeric", minute: "2-digit", hour12: true },
    null
  );
  return text === null ? fallback : text.replace(/\b(am|pm)\b/, (m) => m.toUpperCase());
};

const RELATIVE_UNITS = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["week", 7 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
];

/** "2 days ago" / "in 3 hours" / "just now" */
export const formatRelative = (value, { fallback = DATE_FALLBACK, now = Date.now() } = {}) => {
  const date = parseDate(value);
  if (!date) return fallback;
  const diffSeconds = Math.round((date.getTime() - now) / 1000);
  if (Math.abs(diffSeconds) < 60) return "just now";
  const [unit, seconds] = RELATIVE_UNITS.find(([, size]) => Math.abs(diffSeconds) >= size);
  try {
    return new Intl.RelativeTimeFormat(DATE_LOCALE, { numeric: "auto" }).format(Math.trunc(diffSeconds / seconds), unit);
  } catch {
    return fallback;
  }
};

const pad = (n) => String(n).padStart(2, "0");

/** Value for <input type="datetime-local">, or "" when invalid. */
export const toDateTimeInputValue = (value) => {
  const date = parseDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Comparator for sorting newest first; items without a valid date go last. */
export const compareDatesDesc = (a, b) => {
  const [ta, tb] = [toTimestamp(a), toTimestamp(b)];
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return tb - ta;
};

/** Comparator for sorting oldest first; items without a valid date go last. */
export const compareDatesAsc = (a, b) => {
  const [ta, tb] = [toTimestamp(a), toTimestamp(b)];
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return ta - tb;
};

/** First candidate that parses to a valid date, e.g. pickDate(item.requested_at, item.created_on). */
export const pickDate = (...candidates) => candidates.find((candidate) => isValidDate(candidate)) ?? null;
